import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { followBrand } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Share2, GitCompareArrows, AlertCircle, BadgeCheck } from "lucide-react";
import InlineReportForm from "@/components/brand/InlineReportForm";

/**
 * Right-column sidebar of the brand profile: access-restricted claim CTA,
 * DPP readiness box, follow-by-email form, and the action-button row
 * (compare / report / share). Extracted from BrandProfile.jsx to isolate
 * side-effects (share, follow submit) from the page container.
 */
export default function BrandSidebar({ brand, sector, profile, accessRestricted, crawlPolicy, dpp, onCompareClick }) {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [followState, setFollowState] = useState("idle");
  const [reportOpen, setReportOpen] = useState(false);

  const doFollow = async (e) => {
    e.preventDefault();
    if (!email) return;
    setFollowState("loading");
    try {
      await followBrand({ brand_slug: brand.slug, email, locale });
      setFollowState("pending");
      toast.success(t.follow_pending);
    } catch (err) {
      setFollowState("idle");
      toast.error("Failed to submit");
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: brand.display_name, url });
        return;
      } catch (e) { /* user cancelled */ }
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <aside className="lg:col-span-4 space-y-6">
      {accessRestricted && !brand.claimed_by_brand && crawlPolicy && (
        <div className="p-5 border-2" style={{ borderColor: "#5A7A9A", background: "#F1F5F9" }} data-testid="access-restricted-claim-cta">
          <div className="uppercase text-xs tracking-[0.3em] mb-2 font-bold" style={{ color: "#2A3E56" }}>{t.claim_cta_title}</div>
          <p className="text-sm mb-3" style={{ color: "#2A3E56" }}>{t.claim_cta_body}</p>
          <div className="text-xs font-mono p-2 bg-white border mb-3 break-all" style={{ borderColor: "#B5CADE" }}>
            {t.claim_cta_ua_prefix}<span data-testid="access-restricted-ua">{crawlPolicy.user_agent}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to={`/brands/${brand.slug}/claim`}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
              data-testid="access-restricted-claim-btn"
            >
              <BadgeCheck size={14} className="mr-1.5" />
              {t.claim_cta_button}
            </Link>
            <Link to={crawlPolicy.policy_url} className="inline-flex items-center justify-center px-3 py-2 text-sm border" style={{ borderColor: "#B5CADE", color: "#2A3E56" }} data-testid="access-restricted-crawl-policy-link">
              Crawl policy
            </Link>
          </div>
        </div>
      )}
      <div className="p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="dpp-readiness-box">
        <div className="uppercase text-xs tracking-[0.3em] mb-2" style={{ color: "var(--bp-text-muted)" }}>{t.dpp_readiness}</div>
        <p className="text-base leading-relaxed">{dpp}</p>
        {sector && <div className="mt-3 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Regulation EU 2024/1781 · ESPR</div>}
      </div>

      <div className="p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
        <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>{t.follow_cta}</div>
        {followState === "pending" ? (
          <p className="text-sm" style={{ color: "var(--bp-text-secondary)" }}>{t.follow_pending}</p>
        ) : (
          <form onSubmit={doFollow} className="space-y-3">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.follow_placeholder}
              data-testid="follow-email-input"
            />
            <Button type="submit" className="w-full rounded-none" data-testid="follow-submit">{t.follow_submit}</Button>
          </form>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" className="rounded-none" onClick={onCompareClick} data-testid="btn-compare">
          <GitCompareArrows size={16} className="mr-2" />{t.compare_cta}
        </Button>
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-none" data-testid="btn-report">
              <AlertCircle size={16} className="mr-2" />{t.report_cta}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">{t.report_title}</DialogTitle></DialogHeader>
            <InlineReportForm brandSlug={brand.slug} onDone={() => setReportOpen(false)} />
          </DialogContent>
        </Dialog>
        <Button variant="outline" className="rounded-none" onClick={share} data-testid="btn-share">
          <Share2 size={16} className="mr-2" />{t.share_cta}
        </Button>
      </div>
    </aside>
  );
}
